import type { FieldHook } from 'payload'
import slugify from 'slugify'

export const formatSlug = (val: string): string =>
  slugify(
    val
      .replaceAll('ä', 'ae')
      .replaceAll('ö', 'oe')
      .replaceAll('ü', 'ue')
      .replaceAll('ß', 'ss')
      .replaceAll('/', '')
      .replaceAll('.', '')
      .replaceAll(':', '')
      .replaceAll('?', '')
      .replaceAll('&', '')
      .replaceAll('"', '')
      .replaceAll("'", ''),
    {
      lower: true,
      locale: 'de',
    },
  ).replaceAll('--', '-')

export type SlugFromFieldsConfig = {
  titleField: string
  suffixField?: string
  dateField?: string
}

export function buildSlugFromTitleSuffixYear(
  title: string | undefined,
  suffix: string | undefined,
  date: string | Date | null | undefined,
): string {
  if (!title?.trim()) return ''
  const year = date ? new Date(date).getFullYear() : null
  const base = formatSlug(title)
  const suffixPart =
    suffix && String(suffix).trim() ? '-' + formatSlug(String(suffix).trim()) : ''
  const yearPart = year ? '-' + year : ''
  return base + suffixPart + yearPart
}

export const formatSlugHook =
  (fallback: string): FieldHook =>
  ({ data, operation, originalDoc, value }) => {
    if (typeof value === 'string' && value.length > 0) {
      return formatSlug(value)
    }

    if (operation === 'create' || !data?.slug) {
      const fallbackData = data?.[fallback] || originalDoc?.[fallback]

      if (fallbackData && typeof fallbackData === 'string') {
        return formatSlug(fallbackData)
      }
    }

    return value
  }

export const formatSlugHookFromFields =
  (config: SlugFromFieldsConfig): FieldHook =>
  ({ data, originalDoc, value }) => {
    const get = (key: string) => data?.[key] ?? originalDoc?.[key]
    const title = get(config.titleField) as string | undefined
    const suffix = config.suffixField ? (get(config.suffixField) as string | undefined) : undefined
    const date = config.dateField ? (get(config.dateField) as string | Date | null) : null
    const built = buildSlugFromTitleSuffixYear(title, suffix, date)
    if (built) return built
    if (typeof value === 'string' && value.length > 0) return formatSlug(value)
    return value
  }
